/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox のチケット一括取込で、開発リーダーが決まっている案件を担当メンバーの行へ自動配置する純関数群。仮の所要営業日数（見積もり pt ÷ 当月の期待戦闘力の切り上げ）、その行で既に埋まっている予定の次の営業日という開始位置、営業日数ぶんを覆う暦日数（休息日を跨ぐぶん伸ばす）を算出し、取込 1 件ごとの配置先を決める
 * @graph-connects none
 */
import type { TicketLookupItem } from '../../../../types/project.js';
import type { BlockPlacement, SandboxBlock, SandboxMember } from './types.js';

/**
 * 見積もり pt と期待戦闘力から、仮の所要営業日数を求める。
 *
 * 期待戦闘力は「1 営業日あたりに倒せる pt」なので、pt ÷ 期待戦闘力 が営業日数になる。
 * 半端な日数のまま置けないので切り上げる（35pt ÷ 8 = 4.375 → 5 営業日）。
 * 見積もり未入力（0pt）の案件でも 1 営業日は確保して、行の上で掴める大きさにする。
 *
 * @graph-connects none
 */
export function requiredBusinessDays(sizePt: number, expectedPower: number): number {
  if (!Number.isFinite(expectedPower) || expectedPower <= 0) return 1;
  return Math.max(1, Math.ceil(sizePt / expectedPower));
}

/**
 * `from` 以降で最初の営業日（休息日でない日）の日インデックスを返す。
 * 表示期間の末尾まで営業日が無ければ null。
 * @graph-connects none
 */
export function firstBusinessDayFrom(restRow: readonly boolean[], from: number): number | null {
  for (let day = Math.max(0, from); day < restRow.length; day++) {
    if (!restRow[day]) return day;
  }
  return null;
}

/**
 * 開始日から数えて `businessDays` 営業日ぶんを覆う暦日数（= `spanDays`）を返す。
 *
 * ブロックの長さは暦日で持つため、途中に休息日（土日・稼働日計算タブの稼働割合 0）が挟まる
 * ぶんだけ伸ばす。表示期間内に必要な営業日数を確保できない場合は null を返し、
 * 呼び出し側が「切り詰めて置く」のではなく未配置のままにできるようにする。
 *
 * @graph-connects none
 */
export function spanCoveringBusinessDays(
  restRow: readonly boolean[],
  startDay: number,
  businessDays: number,
): number | null {
  let counted = 0;
  for (let day = startDay; day < restRow.length; day++) {
    if (!restRow[day]) counted += 1;
    if (counted === businessDays) return day - startDay + 1;
  }
  return null;
}

/**
 * メンバー行ごとの「既に埋まっている予定の終端（＝最後のブロックの翌日の日インデックス）」を求める。
 * 未配置ブロックは行を持たないので対象外。
 * @graph-connects none
 */
export function rowOccupiedEnds(blocks: readonly SandboxBlock[]): Map<number, number> {
  const ends = new Map<number, number>();
  for (const block of blocks) {
    const placement = block.placement;
    if (placement === null) continue;
    const end = placement.startDay + placement.spanDays;
    if (end > (ends.get(placement.rowIndex) ?? Number.NEGATIVE_INFINITY)) {
      ends.set(placement.rowIndex, end);
    }
  }
  return ends;
}

/** チケット取込 1 件の配置計画。 */
export interface TicketPlacementPlan {
  /** 取込対象の照会結果。 */
  item: TicketLookupItem;
  /** 自動配置先。null なら配置先を決められなかったので共有トレイ（未配置）へ入れる。 */
  placement: BlockPlacement | null;
}

/** `planTicketPlacements` の入力。 */
export interface PlanTicketPlacementsInput {
  /** 取込対象（既存ブロックとの重複除外済み）。 */
  items: readonly TicketLookupItem[];
  /** 表示中の全ブロック（白板の配置済み + 共有トレイの未配置）。既存予定の終端の判定に使う。 */
  blocks: readonly SandboxBlock[];
  /** 表示中の白板のメンバー行。開発リーダーのニックネームをこの並びへ引き当てる。 */
  members: readonly SandboxMember[];
  /** メンバー行 × 日 の休息日マトリクス（`true` = 休息日）。 */
  restMatrix: readonly (readonly boolean[])[];
  /** ニックネーム（小文字）→ 当月の期待戦闘力。 */
  expectedPowerByNick: ReadonlyMap<string, number>;
  /** 自動配置の下限にする日インデックス（＝今日）。過去には置かない。 */
  earliestDay: number;
}

/**
 * 取込 1 件ごとの自動配置先を決める。
 *
 * 開発リーダーが入っている案件は、その担当メンバーの行の「既存予定の次の営業日」から、
 * 仮の所要営業日数（pt ÷ 当月の期待戦闘力の切り上げ）ぶんを覆う長さで置く。
 * 同じ担当へ複数件を取り込んだときは、先に決めたぶんも埋まっている予定として数えて後ろへ積む。
 *
 * 次のいずれかに当てはまる案件は配置せず（`placement: null`）、従来どおり共有トレイへ落とす。
 * 黙って別の担当・別の長さで置くより、未配置のまま人が置く方が誤りに気づけるため:
 *
 * - 開発リーダーが空
 * - 開発リーダーが表示中の白板の名簿に居ない（別チームの担当・退職者など）
 * - そのメンバーの当月の期待戦闘力が取得できない（＝仮の長さを決められない）
 * - 表示期間（過去 1 週〜未来 4 週）に必要な営業日数ぶんの空きが残っていない
 *
 * @graph-connects rowOccupiedEnds [calls] メンバー行ごとの既存予定の終端の算出
 * @graph-connects firstBusinessDayFrom [calls] 開始日（次の営業日）の算出
 * @graph-connects requiredBusinessDays [calls] 仮の所要営業日数の算出
 * @graph-connects spanCoveringBusinessDays [calls] 営業日数を覆う暦日数の算出
 */
export function planTicketPlacements(input: PlanTicketPlacementsInput): TicketPlacementPlan[] {
  const rowByNick = new Map<string, number>();
  input.members.forEach((member, index) => {
    rowByNick.set((member.nickName ?? member.name).toLowerCase(), index);
  });
  const occupiedEnds = rowOccupiedEnds(input.blocks);

  return input.items.map((item) => {
    const nick = item.devLeader?.trim().toLowerCase() ?? '';
    const rowIndex = nick === '' ? undefined : rowByNick.get(nick);
    if (rowIndex === undefined) return { item, placement: null };

    const restRow = input.restMatrix[rowIndex];
    const expectedPower = input.expectedPowerByNick.get(nick);
    if (restRow === undefined || expectedPower === undefined) return { item, placement: null };

    const from = Math.max(input.earliestDay, occupiedEnds.get(rowIndex) ?? input.earliestDay);
    const startDay = firstBusinessDayFrom(restRow, from);
    if (startDay === null) return { item, placement: null };

    const businessDays = requiredBusinessDays(item.roughPt ?? 0, expectedPower);
    const spanDays = spanCoveringBusinessDays(restRow, startDay, businessDays);
    if (spanDays === null) return { item, placement: null };

    // 同じ担当への次の 1 件が、いま決めたブロックの後ろから始まるようにする。
    occupiedEnds.set(rowIndex, startDay + spanDays);
    return { item, placement: { rowIndex, startDay, spanDays } };
  });
}
